/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Angle,
  Canvas,
  Cursor,
  CursorEvent,
  DEFAULT_ANCHOR_BY_TYPE,
  DEFAULT_THICKNESS,
  DimensionSide,
  DrawingType,
  Element,
  ElementState,
  ElementType,
  Fixture,
  FixtureType,
  Group,
  LineType,
  PageFormat,
  Polyline,
  Rect,
  Role,
  Subscription,
  Symbol,
  SymbolType,
  Text,
  Trapezoid,
  Triangle,
  UserVersion,
} from '@ritabygglov/core';
import { uuid } from './helpers/utility';
import {
  createCreateChange,
  createUpdateChange,
} from './services/Actions/changes';
import { createRect as buildRect } from './services/Actions/Rect';
import {
  AppState,
  DimensionDragInfo,
  ResizeAnchor,
  SelectionModes,
} from './types';

export const cursorStub: Cursor = {
  id: 'unique-id',
  username: 'John Doe',
  position: {
    x: 0,
    y: 0,
  },
  lastUpdated: '2023-03-19T09:31:49.722Z',
  color: 'red',
};
export const cursorEventStub: CursorEvent = {
  type: 'cursor',
  data: [cursorStub],
};

export const eventStub: MessageEvent = {
  data: {
    text: () => JSON.stringify(cursorEventStub),
  },
  lastEventId: '',
  origin: '',
  ports: [],
  source: null,
  initMessageEvent: function (
    _type: string,
    _bubbles?: boolean | undefined,
    _cancelable?: boolean | undefined,
    _data?: unknown,
    _origin?: string | undefined,
    _lastEventId?: string | undefined,
    _source?: MessageEventSource | null | undefined,
    _ports?: MessagePort[] | undefined
  ): void {
    throw new Error('Function not implemented.');
  },
  bubbles: false,
  cancelBubble: false,
  cancelable: false,
  composed: false,
  currentTarget: null,
  defaultPrevented: false,
  eventPhase: 0,
  isTrusted: false,
  returnValue: false,
  srcElement: null,
  target: null,
  timeStamp: 0,
  type: '',
  // @ts-ignore - React 19 changed Event types, stub doesn't need strict typing
  composedPath: function (): EventTarget[] {
    throw new Error('Function not implemented.');
  },
  initEvent: function (
    _type: string,
    _bubbles?: boolean | undefined,
    _cancelable?: boolean | undefined
  ): void {
    throw new Error('Function not implemented.');
  },
  preventDefault: function (): void {
    throw new Error('Function not implemented.');
  },
  stopImmediatePropagation: function (): void {
    throw new Error('Function not implemented.');
  },
  stopPropagation: function (): void {
    throw new Error('Function not implemented.');
  },
  AT_TARGET: 2,
  BUBBLING_PHASE: 3,
  CAPTURING_PHASE: 1,
  NONE: 0,
};

export const userVersion: UserVersion = {
  userId: '123',
  version: 0,
};

export const rectStub: Rect = {
  type: ElementType.Rect,
  thickness: DEFAULT_THICKNESS,
  thicknessAnchor: DEFAULT_ANCHOR_BY_TYPE[ElementType.Rect],
  width: 5,
  height: 5,
  x: 0,
  y: 0,
  id: '',
  state: ElementState.Visible,
  rotate: 0,
  renderingOrder: 0,
  text: '',
  userVersion,
  showDimensions: false,
  diagonalHatch: false,
};

export const textStub: Text = {
  type: ElementType.Text,
  thickness: DEFAULT_THICKNESS,
  thicknessAnchor: DEFAULT_ANCHOR_BY_TYPE[ElementType.Text],
  width: 5,
  height: 5,
  x: 0,
  y: 0,
  id: '',
  state: ElementState.Visible,
  rotate: 0,
  renderingOrder: 0,
  text: '',
  userVersion,
  showDimensions: false,
  diagonalHatch: false,
};

export const defaultUser = {
  name: '',
  email: '',
  roles: [Role.User],
  isSubscribed: false,
  isAcceptingMarketing: false,
  locale: 'sv' as const,
  site: 'ritabygglov' as const,
  userId: 0,
  canvases: [],
  checklists: [],
  subscriptions: [],
  createdAt: '',
  lastSeenAt: '',
  activeDays: 0,
};

export const activeSubscriptionStub: Subscription = {
  subscriptionId: 'sub-1',
  userId: 1,
  stripeCustomerId: 'cus-1',
  stripeSubscriptionId: 'stripe-sub-1',
  stripeSessionId: null,
  status: 'active',
  currentPeriodStart: '2026-02-01T00:00:00.000Z',
  currentPeriodEnd: '2026-04-01T00:00:00.000Z',
  cancelAt: '',
  cancelAtPeriodEnd: false,
  createdAt: '2026-02-01T00:00:00.000Z',
  updatedAt: '2026-02-01T00:00:00.000Z',
};

export const canceledSubscriptionStub: Subscription = {
  ...activeSubscriptionStub,
  subscriptionId: 'sub-2',
  status: 'canceled',
  currentPeriodStart: '2025-01-01T00:00:00.000Z',
  currentPeriodEnd: '2025-12-31T00:00:00.000Z',
};

export const canvasStub: Canvas = {
  canvasId: '123456789',
  userId: 0,
  name: '',
  drawingType: DrawingType.SITE_PLAN,
  commune: '',
  createdAt: '',
  updatedAt: '',
  mapUrl: '',
  author: '',
  pageFormat: PageFormat.A3_LANDSCAPE,
  authorExportedAt: '',
  drawingHeadScale: '',
  drawingHeadPropertyReference: '',
  northArrowRotation: 0,
  showNorthArrow: false,
  drawingHeadType: '',
  showDrawingHead: false,
  unitSystem: 'metric',
  lengthFormat: 'meters',
};

export const createRect = createCreateChange(
  buildRect(0, 0, 1, 'unique-id', 'b')
);
export const updatedRect = createUpdateChange(
  createRect.object as Rect,
  false,
  createRect.object.userVersion.userId
);

export const oldUpdateAction = {
  ...updatedRect,
  object: {
    ...updatedRect.object,
    userVersion: {
      version: 0,
      userId: updatedRect.object.userVersion.userId,
    },
  },
};

export const concurrentObject = {
  ...updatedRect.object,
  userVersion: {
    version: 1,
    userId: 'a',
  },
};

export const concurrentChange = {
  ...updatedRect,
  object: concurrentObject,
};

export const cursorChange = {
  id: 'unique-user-id',
  username: 'John Doe',
  color: '#fff',
  position: { x: 100, y: 100 },
  lastUpdated: '2023-03-19T09:31:49.722Z',
};

export const canvasId = uuid();

export const appStateStub: AppState = {
  elements: { ['unique-id']: createRect.object },
  cursors: {},
  map: undefined,
};

export const rectElementStub: Rect = {
  id: 'rect-1',
  type: ElementType.Rect,
  thickness: DEFAULT_THICKNESS,
  thicknessAnchor: DEFAULT_ANCHOR_BY_TYPE[ElementType.Rect],
  x: 100,
  y: 100,
  width: 200,
  height: 150,
  rotate: 0,
  diagonalHatch: false,
  pattern: 'none',
  renderingOrder: 1,
  showDimensions: false,
  state: ElementState.Visible,
  text: '',
  userVersion: { userId: 'user-1', version: 1 },
};

export const polylineElementStub: Polyline = {
  id: 'polyline-1',
  type: ElementType.Polyline,
  thickness: DEFAULT_THICKNESS,
  thicknessAnchor: DEFAULT_ANCHOR_BY_TYPE[ElementType.Polyline],
  points: [
    { x: 0, y: 0, dimensionSide: DimensionSide.Below },
    { x: 100, y: 100, dimensionSide: DimensionSide.Below },
  ],
  pattern: 'none',
  renderingOrder: 1,
  showDimensions: false,
  state: ElementState.Visible,
  text: '',
  userVersion: { userId: 'user-1', version: 1 },
  lineType: LineType.Line,
  isOutlined: false,
  stroke: '#000000',
  diagonalHatch: false,
  rotate: 0,
};

export const measurementLineElementStub: Polyline = {
  ...polylineElementStub,
  id: 'measurement-line-1',
  lineType: LineType.Dimension,
  showDimensions: true,
};

export const textElementStub: Text = {
  id: 'text-1',
  type: ElementType.Text,
  thickness: DEFAULT_THICKNESS,
  thicknessAnchor: DEFAULT_ANCHOR_BY_TYPE[ElementType.Text],
  x: 100,
  y: 100,
  width: 200,
  height: 50,
  rotate: 0,
  diagonalHatch: false,
  pattern: 'none',
  renderingOrder: 1,
  showDimensions: false,
  state: ElementState.Visible,
  text: 'Sample text',
  userVersion: { userId: 'user-1', version: 1 },
};

export const fixtureElementStub: Fixture = {
  id: 'fixture-1',
  type: ElementType.Fixture,
  thickness: DEFAULT_THICKNESS,
  thicknessAnchor: DEFAULT_ANCHOR_BY_TYPE[ElementType.Fixture],
  x: 100,
  y: 100,
  width: 150,
  height: 200,
  rotate: 0,
  fixtureType: FixtureType.WINDOW_PLAN,
  mirroredX: false,
  mirroredY: false,
  pattern: 'none',
  renderingOrder: 1,
  showDimensions: false,
  state: ElementState.Visible,
  text: '',
  userVersion: { userId: 'user-1', version: 1 },
};

export const symbolElementStub: Symbol = {
  id: 'symbol-1',
  type: ElementType.Symbol,
  thickness: DEFAULT_THICKNESS,
  thicknessAnchor: DEFAULT_ANCHOR_BY_TYPE[ElementType.Symbol],
  x: 100,
  y: 100,
  width: 16,
  height: 16,
  rotate: 0,
  symbolType: SymbolType.GENERAL_WELL,
  pattern: 'none',
  renderingOrder: 1,
  showDimensions: false,
  state: ElementState.Visible,
  text: '',
  userVersion: { userId: 'user-1', version: 1 },
};

export const selectionCoordinatesStub = {
  currentX: null as number | null,
  currentY: null as number | null,
  initialX: null as number | null,
  initialY: null as number | null,
  startX: null as number | null,
  startY: null as number | null,
  initialWidth: null as number | null,
  initialHeight: null as number | null,
  originElements: [] as Element[],
  xOffset: 0,
  yOffset: 0,
  selectedAnchor: null as null | ResizeAnchor | number,
  nextPointIndex: 0,
};

export const viewBoxStub = {
  x: 0,
  y: 0,
  w: 1000,
  h: 1000,
  scale: 1,
  startPoint: { x: 0, y: 0 },
  endPoint: { x: 0, y: 0 },
};

export const selectionModeStub = {
  type: SelectionModes.None,
  elementType: undefined,
  lineType: undefined,
};

export const ALL_ELEMENT_TYPES = [
  ElementType.Rect,
  ElementType.Text,
  ElementType.Polyline,
  ElementType.Symbol,
  ElementType.Fixture,
] as const;

export const SHAPE_ELEMENT_TYPES = [
  ElementType.Rect,
  ElementType.Symbol,
  ElementType.Fixture,
] as const;

export const NON_TEXT_ELEMENT_TYPES = [
  ElementType.Rect,
  ElementType.Polyline,
  ElementType.Symbol,
] as const;

export const NON_FIXTURE_ELEMENT_TYPES = [
  ElementType.Rect,
  ElementType.Text,
  ElementType.Polyline,
  ElementType.Symbol,
] as const;

export const createRectElement = (overrides?: Partial<Rect>): Rect => ({
  ...rectElementStub,
  ...overrides,
});

export const triangleElementStub: Triangle = {
  id: 'triangle-1',
  type: ElementType.Triangle,
  thickness: DEFAULT_THICKNESS,
  thicknessAnchor: DEFAULT_ANCHOR_BY_TYPE[ElementType.Triangle],
  x: 100,
  y: 100,
  width: 200,
  height: 150,
  pointRatio: 0.5,
  rotate: 0,
  diagonalHatch: false,
  renderingOrder: 1,
  showDimensions: false,
  state: ElementState.Visible,
  text: '',
  userVersion: { userId: 'user-1', version: 1 },
};

export const createTriangleElement = (
  overrides?: Partial<Triangle>
): Triangle => ({
  ...triangleElementStub,
  ...overrides,
});

export const trapezoidElementStub: Trapezoid = {
  id: 'trapezoid-1',
  type: ElementType.Trapezoid,
  thickness: DEFAULT_THICKNESS,
  thicknessAnchor: DEFAULT_ANCHOR_BY_TYPE[ElementType.Trapezoid],
  x: 100,
  y: 100,
  width: 150,
  topWidth: 100,
  bottomWidth: 150,
  height: 80,
  rotate: 0,
  diagonalHatch: false,
  renderingOrder: 1,
  showDimensions: false,
  state: ElementState.Visible,
  text: '',
  userVersion: { userId: 'user-1', version: 1 },
};

export const createTrapezoidElement = (
  overrides?: Partial<Trapezoid>
): Trapezoid => ({
  ...trapezoidElementStub,
  ...overrides,
});

export const angleElementStub: Angle = {
  id: 'angle-1',
  type: ElementType.Angle,
  thickness: DEFAULT_THICKNESS,
  thicknessAnchor: DEFAULT_ANCHOR_BY_TYPE[ElementType.Angle],
  x: 100,
  y: 100,
  width: 200,
  height: 150,
  pointRatio: 0.5,
  rotate: 0,
  diagonalHatch: false,
  renderingOrder: 1,
  showDimensions: false,
  state: ElementState.Visible,
  text: '',
  userVersion: { userId: 'user-1', version: 1 },
};

export const createAngleElement = (overrides?: Partial<Angle>): Angle => ({
  ...angleElementStub,
  ...overrides,
});

export const createPolylineElement = (
  overrides?: Partial<Polyline>
): Polyline => ({
  ...polylineElementStub,
  ...overrides,
});

export const createWidthDragInfo = (
  overrides?: Partial<DimensionDragInfo>
): DimensionDragInfo => ({
  elementId: 'rect-1',
  dimensionType: 'width',
  startOffset: 0,
  startMouseX: 0,
  startMouseY: 0,
  ...overrides,
});

export const createHeightDragInfo = (
  overrides?: Partial<DimensionDragInfo>
): DimensionDragInfo => ({
  elementId: 'rect-1',
  dimensionType: 'height',
  startOffset: 0,
  startMouseX: 0,
  startMouseY: 0,
  ...overrides,
});

export const createSegmentDragInfo = (
  overrides?: Partial<DimensionDragInfo>
): DimensionDragInfo => ({
  elementId: 'polyline-1',
  dimensionType: 'segment',
  segmentIndex: 0,
  startOffset: 0,
  startMouseX: 0,
  startMouseY: 0,
  ...overrides,
});

export const groupElementStub: Group = {
  id: 'group-1',
  type: ElementType.Group,
  x: 100,
  y: 100,
  width: 300,
  height: 250,
  childIds: ['rect-1', 'rect-2'],
  rotate: 0,
  renderingOrder: 1,
  showDimensions: false,
  state: ElementState.Visible,
  text: '',
  userVersion: { userId: 'user-1', version: 1 },
  thickness: DEFAULT_THICKNESS,
  thicknessAnchor: DEFAULT_ANCHOR_BY_TYPE[ElementType.Group],
};

export const createGroupElement = (overrides?: Partial<Group>): Group => ({
  ...groupElementStub,
  ...overrides,
});
